import { EventCardDto, EventStatus } from "@garther/interfaces";
import { Badge, Image, Spinner, Table, TableColumn } from "@garther/ui/components";
import { cn, useI18n } from "@garther/ui/libs";
import { useNavigate } from "@tanstack/react-router";
import { Calendar, MapPin, Ticket } from "lucide-react";
import { useEffect, useMemo } from "react";

import { EventCard } from "@/features/event/components/event-card";
import { useEventsPagination } from "@/features/event/hooks/use-events-query";

interface EventsTableProps {
  hostId: string;
  limit?: number;
  statusFilter?: EventStatus | null;
  search?: string;
  viewMode?: "list" | "grid";
}

export function EventsTable({
  hostId,
  limit = 20,
  statusFilter,
  search,
  viewMode = "list"
}: EventsTableProps) {
  const navigate = useNavigate();
  const { formatDate, t, formatCredit } = useI18n();

  const { events, isLoading, pagination, setPage, currentPage } = useEventsPagination({
    enabled: true,
    hostId,
    sortBy: "createdAt",
    status: statusFilter
      ? [statusFilter]
      : [EventStatus.AVAILABLE, EventStatus.LOCKED, EventStatus.FINISHED, EventStatus.CANCELED],
    search: search || undefined,
    limit
  });

  useEffect(() => {
    setPage(1);
  }, [statusFilter, search, setPage]);

  const getStatusBadge = (status: EventStatus) => {
    const statusConfig: Record<
      EventStatus,
      {
        variant: "default" | "secondary" | "outline";
        className?: string;
        label: string;
      }
    > = {
      AVAILABLE: {
        variant: "default",
        label: t("event.status.available"),
        className: "bg-success-200 text-green-900"
      },
      LOCKED: {
        variant: "secondary",
        label: t("event.status.locked"),
        className: "bg-yellow-200 text-yellow-900"
      },
      FINISHED: {
        variant: "outline",
        label: t("event.status.finished"),
        className: "bg-gray-200 text-gray-900"
      },
      CANCELED: {
        variant: "outline",
        label: t("event.status.canceled"),
        className: "bg-red-200 text-red-900"
      }
    };

    const config = statusConfig[status] || statusConfig.AVAILABLE;

    return (
      <Badge variant={config.variant} className={cn("font-normal text-responsive-base", config.className)}>
        {config.label}
      </Badge>
    );
  };

  const columns: TableColumn<EventCardDto.Type>[] = useMemo(() => {
    const baseConfig: TableColumn<EventCardDto.Type>[] = [
      {
        key: "title",
        title: t("event.table.title"),
        dataIndex: "title",
        width: "30%",
        sortable: true,
        render: (value, record) => (
          <div className="flex items-center gap-1.5 py-2">
            <div className="w-12 h-12 min-w-12">
              <Image className="h-12 w-12 rounded-lg object-cover" src={record.banner} alt="event banner" />
            </div>
            <span className="text-responsive-base font-normal text-text-primary line-clamp-2">{value}</span>
          </div>
        )
      },
      {
        key: "location",
        title: t("event.table.location"),
        dataIndex: "location",
        width: "20%",
        render: (value) => (
          <div className="flex items-start gap-1.5">
            <MapPin className="h-4 w-4 shrink-0 mt-0.5 text-text-secondary" />
            <span className="text-responsive-base text-text-primary line-clamp-2">
              {value?.name || "TBA"}
            </span>
          </div>
        )
      },
      {
        key: "nextSession",
        title: t("event.table.nextSession"),
        dataIndex: "stats",
        width: "20%",
        sortable: true,
        render: (stats: EventCardDto.Type["stats"]) => {
          if (!stats.nextSessionAt) {
            return <span className="text-sm text-text-secondary">{t("event.noSession")}</span>;
          }

          return (
            <div className="flex items-start gap-1.5">
              <Calendar className="h-4 w-4 shrink-0 mt-0.5 text-text-secondary" />
              <div className="flex flex-col">
                <span className="text-responsive-base text-text-primary">
                  {formatDate(stats.nextSessionAt, {
                    month: "short",
                    day: "numeric",
                    year: "numeric"
                  })}
                </span>
                <span className="text-responsive-base text-text-secondary">
                  {formatDate(stats.nextSessionAt, {
                    hour: "2-digit",
                    minute: "2-digit"
                  })}
                </span>
              </div>
            </div>
          );
        }
      },
      {
        key: "status",
        title: t("event.table.status"),
        dataIndex: "status",
        width: "12%",
        sortable: true,
        render: (value) => getStatusBadge(value as EventStatus)
      },
      {
        key: "tickets",
        title: t("event.table.availableTickets"),
        dataIndex: "stats",
        width: "12%",
        align: "center",
        render: (stats: EventCardDto.Type["stats"]) => (
          <div className="flex items-center gap-1.5">
            <Ticket className="h-5 w-5 shrink-0 text-text-secondary" />
            <span className="text-responsive-base font-normaml text-text-primary">
              {stats.ticketsAvailable}
            </span>
          </div>
        )
      }
    ];
    return baseConfig.map((column) => ({
      ...column,
      headerClassName: "text-responsive-base py-4"
    }));
  }, [formatDate, formatCredit, t]);

  const handleRowClick = (record: EventCardDto.Type) => {
    navigate({
      to: "/org/$orgId/event/$eventId",
      params: { orgId: hostId, eventId: record.id }
    });
  };

  if (isLoading) {
    return (
      <div className="w-full min-h-[50vh] flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="w-full py-12 border border-border rounded-lg bg-background-secondary/30">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="mb-4">
            <div className="w-16 h-16 mx-auto bg-background-dark-secondary/10 rounded-full flex items-center justify-center">
              <Calendar className="w-8 h-8 text-text-secondary" strokeWidth={1.5} />
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-lg font-medium text-text-primary">{t("org.events.empty.title")}</p>
            <p className="text-sm text-text-secondary max-w-md">{t("org.events.empty.description")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (viewMode === "grid") {
    return (
      <div className="w-full space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onSelect={(id) =>
                navigate({
                  to: "/org/$orgId/event/$eventId",
                  params: { orgId: hostId, eventId: id }
                })
              }
            />
          ))}
        </div>
        {pagination && pagination.totalPages > 1 && (
          <div className="flex justify-center gap-2 pt-2">
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setPage(page)}
                className={cn(
                  "w-8 h-8 rounded-full text-responsive-sm font-medium transition-colors",
                  currentPage === page
                    ? "bg-background-dark text-background-secondary"
                    : "text-text-secondary hover:bg-background-highlight"
                )}
              >
                {page}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full space-y-4 border border-border-light rounded-xl">
      <Table
        data={events}
        columns={columns}
        rowKey="id"
        hoverable
        size="middle"
        rowClassName="cursor-pointer"
        className="rounded-xl bg-background-secondary"
        onRow={(record) => ({
          onClick: () => handleRowClick(record)
        })}
        pagination={
          pagination
            ? {
                current: currentPage,
                pageSize: limit,
                total: pagination.total,
                onChange: (page) => setPage(page)
              }
            : undefined
        }
      />
    </div>
  );
}
